import type { CommandResult } from '@travel-planner/shared/schemas';
import { applyFiltersResultData } from '@travel-planner/shared/schemas';
import { CheckCircle2 } from 'lucide-react';
import { Text } from '@/shared/ui';
import { parseResultData } from '../model/parse-result-data';
import css from './apply-filters-confirmation.module.scss';

export type ApplyFiltersConfirmationProps = {
  result: CommandResult;
};

// Filter keys that don't carry a meaningful "filter" semantics for the
// consultant (offsets, mergeMode hints, internal helpers). Hidden from
// the chip's per-row breakdown; the chip falls back to the server-built
// summary when nothing meaningful remains.
const HIDDEN_FILTER_KEYS = new Set(['offset', 'limit', 'mergeMode']);

// Friendly label for each known filter key. Falls back to the raw key
// (Title-Cased) when a key isn't listed — so adding a new filter on the
// server side renders sensibly without needing to edit this map.
const FILTER_LABELS: Record<string, string> = {
  search: 'Search',
  contentCategory: 'Category',
  country: 'Country',
  contentSubcategory: 'Subcategory',
  itineraries: 'Itineraries',
  itineraryPriorityLevel: 'Priority',
  operationStatuses: 'Status',
  startDate: 'From',
  endDate: 'To',
  searchField: 'Searching',
};

const titleCase = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

// Render one filter's value as a string. Arrays are joined with ` + ` so
// chip values like ['Accommodation', 'Activity'] render as
// "Accommodation + Activity"; scalars render verbatim.
const formatFilterValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const cleaned = value
      .map((v) => (typeof v === 'string' ? v : String(v)))
      .filter((v) => v.length > 0);
    return cleaned.length > 0 ? cleaned.join(' + ') : null;
  }
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

type FilterRow = { key: string; label: string; value: string };

const buildFilterRows = (
  filters: Record<string, unknown> | undefined
): FilterRow[] => {
  if (!filters) return [];
  const rows: FilterRow[] = [];
  for (const [key, raw] of Object.entries(filters)) {
    if (HIDDEN_FILTER_KEYS.has(key)) continue;
    const value = formatFilterValue(raw);
    if (!value) continue;
    rows.push({
      key,
      label: FILTER_LABELS[key] ?? titleCase(key),
      value,
    });
  }
  return rows;
};

// In-panel renderer for `kind: 'apply_filters'` results. Visible only for
// the brief moment between the result landing and the panel auto-closing
// (PANEL_CLOSE_DELAY_MS in handle-page-action.ts). Undo lives in the
// global toast, not here — see `.claude/rules/agent-panel-page-actions.md`.
//
// Layout:
//   ✓ Applied:
//      • Search   "Porto"
//      • Category Accommodation + Activity + Attraction
//      • Country  Portugal
//
// Falls back to the server-built one-line summary when the parser can't
// extract `filters` (e.g. unexpected data shape) so consultants still see
// SOMETHING confirming the action landed.
export const ApplyFiltersConfirmation = ({
  result,
}: ApplyFiltersConfirmationProps) => {
  const data = parseResultData(result, applyFiltersResultData);
  const rows = buildFilterRows(data?.filters);
  const fallback = data?.summary ?? result.humanSummary;

  return (
    <div className={css.root} role="status">
      <div className={css.heading}>
        <CheckCircle2
          aria-hidden="true"
          size={16}
          style={{ color: 'var(--color-success)', flexShrink: 0 }}
        />
        <Text size="sm" weight="medium">
          Applied:
        </Text>
      </div>
      {rows.length > 0 ? (
        <ul className={css.rows}>
          {rows.map((row) => (
            <li className={css.row} key={row.key}>
              <span className={css.rowLabel}>{row.label}</span>
              <span className={css.rowValue}>{row.value}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Text size="sm">{fallback}</Text>
      )}
    </div>
  );
};
